/**
 * RBAC-4.3 · NFR-10. A revocation reaches the screen promptly, not at the next cache expiry.
 *
 * ⚠️ THIS IS NOT THE SECURITY BOUNDARY. The database already refuses a removed person's very next
 * request: `home_tenant_id()` and `cost_holds_permission_for()` deny an inactive identity, and the
 * SUB-1.37 trigger bans it, revokes its refresh tokens and deletes its sessions
 * (`verify_rbac_4_3_remove_access_and_handover.sql` T03). What this decides is whether the CLIENT
 * keeps showing buttons the API will decline.
 *
 * Two caches stand in the way, and the PRD undersold the first:
 *   - `PermissionContext`'s module cache says fifteen minutes, but the provider fetches once per user
 *     and account and then holds that answer for the life of the page. Nothing refetched it.
 *   - `tenantSettingsService` holds settings for five minutes.
 *
 * So once a minute, on focus and when the tab becomes visible (the schedule `TenantContext` already
 * revalidates scope on), the client asks `my_access_state()`: the caller's own active flag and an md5
 * of the permissions their roles carry. A changed fingerprint refetches; `active: false` signs out.
 *
 * Pure: no DOM, no Supabase. The wiring is `PermissionContext.tsx`.
 */

import { DEACTIVATED_MESSAGE } from './accountStatus';

export const MY_ACCESS_STATE_RPC = 'my_access_state';

/** Same as `TenantContext`'s scope revalidation, so a visible tab makes its two checks together. */
export const ACCESS_REVALIDATION_INTERVAL_MS = 60_000;

export interface AccessState {
  active: boolean;
  fingerprint: string;
}

/** The RPC's jsonb → state. `{active: null}` (no users row) and anything unrecognised are null. */
export function parseAccessState(raw: unknown): AccessState | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.active !== 'boolean' || typeof r.fingerprint !== 'string') return null;
  return { active: r.active, fingerprint: r.fingerprint };
}

export type AccessRevalidation =
  | { action: 'none'; fingerprint: string | null }
  | { action: 'refetch'; fingerprint: string }
  | { action: 'sign_out'; fingerprint: string; reason: string };

/**
 * What to do with a reading, given the fingerprint last seen.
 *
 * ⚠️ AN UNREADABLE ANSWER CHANGES NOTHING. Failing closed here would sign everybody out on a network
 * blip, and it buys no security: the database is what refuses. Same argument as `accountStatus.ts`.
 */
export function decideAccessRevalidation(previous: string | null, state: AccessState | null): AccessRevalidation {
  if (!state) return { action: 'none', fingerprint: previous };
  if (!state.active) return { action: 'sign_out', fingerprint: state.fingerprint, reason: DEACTIVATED_MESSAGE };
  if (previous === null || previous === state.fingerprint) return { action: 'none', fingerprint: state.fingerprint };
  return { action: 'refetch', fingerprint: state.fingerprint };
}
